package dev.lingvo.site.pages

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.site.components.marketing.DocEntry
import dev.lingvo.site.components.marketing.DocsWelcome
import dev.lingvo.site.components.marketing.DocsCode
import dev.lingvo.site.components.marketing.DocsInlineCode
import dev.lingvo.site.components.marketing.DocsInlineLink
import dev.lingvo.site.components.marketing.DocsList
import dev.lingvo.site.components.marketing.DocsProse
import dev.lingvo.site.components.marketing.DocsSubheading
import dev.lingvo.site.components.marketing.MarketingDocPage
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LTable
import dev.lingvo.site.ds.LTableBody
import dev.lingvo.site.ds.LTableCell
import dev.lingvo.site.ds.LTableHead
import dev.lingvo.site.ds.LTableHeaderCell
import dev.lingvo.site.ds.LTableRow
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.monoFont
import dev.lingvo.site.ds.toSitePalette
import kotlinx.browser.document
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.dom.Text
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tr

/**
 * The documentation everything else links to.
 *
 * It mirrors [docs/CLI.md](../../../../../../docs/CLI.md) rather than replacing it: the repository
 * copy is what a developer reads in the terminal, this is what a prospect reads before signing up.
 * Both are written from the same behaviour, so a change to one is a change to the other.
 *
 * Every sentence a reader sees goes through [t]. The shell and YAML inside [DocsCode] deliberately
 * do not: a translated `brew install` is a command that does not run, and a translated `--dry-run`
 * is a flag the CLI rejects. The same line separates the command and flag columns of the tables
 * below from the sentence beside them.
 */
@Page("/docs")
@Composable
fun DocsPage() {
    LaunchedEffect(Unit) {
        document.title = tr("docs.docs_lingvo", "Docs — Lingvo")
    }
    MarketingDocPage(
        eyebrow = t("docs.documentation", "Documentation"),
        title = t("docs.five_minutes_from_an_empty_project", "A clear path from strings to shipped."),
        lede = t("docs.lingvo_is_driven_from_the_command", "Set up your repository, connect your coding agent, and bring reviewed translations back to your app."),
        entries = docEntries(),
        overview = { DocsWelcome() },
    )
}

private fun docEntries(): List<DocEntry> = listOf(
    DocEntry("reference", tr("docs.reference_heading", "Guides and reference")) {
        DocsProse { DocsInlineLink("/docs/cli", t("docs.cli_guide_link", "CLI guide: setup, sync, translation, CI and troubleshooting")) }
        DocsProse { DocsInlineLink("/docs/cli/commands", t("docs.cli_commands_link", "Every CLI command and option")) }
        DocsProse { DocsInlineLink("/docs/mcp", t("docs.mcp_reference_link", "MCP guide: local and hosted setup, tool schemas, permissions and examples")) }
        DocsProse { DocsInlineLink("/docs/stores", t("docs.stores_guide_link", "Store listings guide: translate and push your App Store and Google Play pages")) }
        DocsProse { DocsInlineLink("/docs/screenshots", t("docs.screenshots_guide_link", "Screenshots guide: plan, capture, design, review, push and export")) }
        DocsProse { DocsInlineLink("/store-audit", t("docs.store_audit_link", "Free check: which App Store storefronts still show your app in English")) }
        DocsProse(t("docs.new_account_setup", "Create an account first, then pair the CLI and configure the repository. init stays offline; project create creates a hosted project in the workspace you name. Choose your own workspace slug and target languages."))
        DocsCode("lingvo login --device\nlingvo init\nlingvo project create --workspace my-workspace --locale de --locale ja\nlingvo push")
    },
    DocEntry("install", tr("docs.install", "Install")) {
        // The runtime requirement is stated once, next to the install that needs one already
        // present. It used to be said three times in this one section, which reads less like
        // emphasis than like a page that has lost track of what it has already told you.
        DocsProse(t("docs.on_macos_homebrew_installs_the_java", "On macOS, Homebrew installs the Java 21 runtime with it, so this is the whole thing:"))
        DocsCode("brew install merkost/lingvo/lingvo")
        DocsProse(t("docs.everywhere_else_an_install_script_that", "Everywhere else, an install script that verifies its own download and writes only inside your home directory. It needs a Java 21 runtime already present — `actions/setup-java@v4` in CI, or your distribution's `openjdk-21-jre-headless` — and checks for one before it downloads anything."))
        DocsCode("curl -fsSL https://lingvo.dev/install.sh | sh")
        DocsProse(t("docs.both_land_the_same_build_pin", "Use CLI 0.4.0 or later for this guide. To pin the shell installer to 0.4.0, set LINGVO_VERSION on the sh command as shown below. Versioned downloads keep CI on the release you tested."))
        DocsCode("curl -fsSL https://lingvo.dev/install.sh | LINGVO_VERSION=0.4.0 sh\nexport PATH=\"\$HOME/.local/bin:\$PATH\"\nlingvo --version")
        DocsProse(t("docs.none_of_this_is_required_to", "None of this is required to try Lingvo: importing and exporting live on each project's Data page. The CLI is for putting that same loop in CI."))
    },

    DocEntry("authenticate", tr("docs.authenticate", "Authenticate")) {
        DocsProse(t("docs.device_first", "For interactive setup, run lingvo login --device and approve your own pairing in the browser. For CI, use a project-scoped token with the permissions the job needs."))
        DocsCode("lingvo login --device")
        DocsProse(t("docs.create_a_token_in_the_web", "Create a token in the web app under Settings → API tokens. It is shown exactly once. Credentials resolve in a fixed order and the first hit wins."))
        LTable(caption = t("docs.credential_sources_in_resolution_order", "Credential sources, in resolution order")) {
            LTableHead {
                LTableRow {
                    LTableHeaderCell { Text(t("docs.source", "Source")) }
                    LTableHeaderCell { Text(t("docs.how_to_set_it", "How to set it")) }
                    LTableHeaderCell { Text(t("docs.use_when", "Use when")) }
                }
            }
            LTableBody {
                CredentialSource(
                    "LINGVO_TOKEN",
                    "export LINGVO_TOKEN=lgo_…",
                    t("docs.ci_containers_agents_nothing_is_written", "CI, containers, agents. Nothing is written to disk."),
                )
                CredentialSource(
                    "~/.lingvo/credentials",
                    "lingvo login --token-stdin",
                    t("docs.a_developers_own_machine_the_file", "A developer's own machine. The file is created mode 600."),
                )
            }
        }
        DocsSubheading(t("docs.logging_in_without_leaking_the_secret", "Logging in without leaking the secret"))
        DocsProse(t("docs.piping_the_token_in_means_it", "Piping the token in means it never reaches argv, shell history or the process list. Passing it as a flag works and warns, because those three places all keep a copy."))
        DocsCode(
            """
            # Best: never touches argv, history, or the process list
            printf '%s' "${'$'}LINGVO_API_TOKEN" | lingvo login --token-stdin

            # Also fine: read from a named environment variable
            lingvo login --token-env LINGVO_API_TOKEN

            # Check what would be used — no network call, never prints the token
            lingvo auth status
            """.trimIndent(),
        )
    },

    DocEntry("configure", tr("docs.configure_lingvo_yaml", "Configure — lingvo.yaml")) {
        DocsProse(t("docs.the_config_lives_at_the_repository", "The config lives at the repository root. Running lingvo init writes one for you by detecting the localization files already present and inferring the project kind."))
        DocsCode(
            """
            project: my-project-slug        # required — the project slug in Lingvo
            source_locale: en               # required — the locale your source files are written in

            files:                          # required — at least one entry
              - source: composeResources/values/strings.xml
                translation: composeResources/values-%locale%/strings.xml
                format: android_xml

              - source: iosApp/Localizable.strings
                translation: iosApp/%locale%.lproj/Localizable.strings
                format: ios_strings

            generate:                       # optional — typed key accessors
              target: kotlin-object         # kotlin-object | swift-enum
              output: shared/src/commonMain/kotlin/app/Strings.kt
              package: app                  # kotlin-object only
            """.trimIndent(),
        )
        DocsProse(t("docs.locale_is_replaced_with_each_target", "%locale% is replaced with each target locale. Android uses its own qualifier form (values-pt-rBR), which lingvo init writes correctly for android_xml paths."))
        LAlert(
            title = t("docs.commit_lingvo_lock_json", "Commit .lingvo-lock.json"),
            description = t("docs.it_is_written_next_to_lingvo", "It is written next to lingvo.yaml and records the content hash of every synced file. Without it, sync cannot tell \"unchanged\" from \"edited locally\", and a local edit gets silently overwritten."),
            modifier = Modifier.fillMaxWidth().maxWidth(44.cssRem),
            variant = LAlertVariant.INFO,
        )
    },

    DocEntry("sync", tr("docs.the_sync_loop", "The sync loop")) {
        DocsProse(t("docs.three_commands_cover_day_to_day", "Three commands cover day-to-day use. None of them is metered on any plan — Lingvo does not charge for keys, languages or words."))
        CommandTable(
            caption = t("docs.everyday_commands", "Everyday commands"),
            rows = listOf(
                CommandRow(
                    "lingvo status",
                    "",
                    t("docs.per_locale_progress_for_the_project", "Per-locale progress for the project."),
                ),
                CommandRow(
                    "lingvo push",
                    "--dry-run --file --keep-obsolete --confirm-obsolete --translations --locale",
                    t("docs.uploads_source_files_as_keys_keys", "Uploads source files as keys. Keys absent from the payload are marked obsolete unless --keep-obsolete. A push dropping 5+ keys and over 20% of a file is refused until you pass --confirm-obsolete with the exact number the dry run reported."),
                ),
                CommandRow(
                    "lingvo pull",
                    "--locale --fallback --format",
                    t("docs.writes_translated_files_to_the_configured", "Writes translated files to the configured paths. Untranslated keys are omitted, never written empty."),
                ),
                CommandRow(
                    "lingvo sync",
                    "--dry-run --adopt-local --locale --fallback --keep-obsolete --confirm-obsolete",
                    t("docs.push_then_pull_in_one_pass", "push then pull in one pass. A translation file edited locally is reported as a conflict; --adopt-local uploads it instead of overwriting. The obsoletion guard applies exactly as it does to push, so a release that deletes a screen needs --confirm-obsolete or --keep-obsolete."),
                ),
                CommandRow(
                    "lingvo scan",
                    "--include --snippets --dry-run",
                    t(
                        "docs.records_where_each_string_is_used",
                        "Records where each string is used in your source, so a translator sees " +
                            "checkout/CartScreen.kt:214 beside the string. Paths and line numbers " +
                            "only — pass --snippets to also send the matching line of code. " +
                            "Vendored and build directories, generated lines and your own " +
                            "translation files are skipped. Re-running replaces what was there, so " +
                            "a string that has left the code stops being listed.",
                    ),
                ),
                CommandRow(
                    "lingvo watch",
                    "",
                    t("docs.re_pushes_source_files_when_they", "Re-pushes source files when they change. Interactive dev loop only — not for CI."),
                ),
            ),
        )
        LAlert(
            title = t("docs.always_dry_run_the_first_push", "Always --dry-run the first push in an unfamiliar repo"),
            description = t("docs.without_keep_obsolete_keys_that_exist", "Without --keep-obsolete, keys that exist in Lingvo but not in the pushed file are marked obsolete. The dry run prints the exact counts, including obsoletions, and changes nothing."),
            modifier = Modifier.fillMaxWidth().maxWidth(44.cssRem),
            variant = LAlertVariant.WARNING,
        )
        DocsSubheading(t("docs.reading_the_counts", "Reading the counts"))
        DocsProse(t("docs.every_push_and_sync_reports_five", "Every push and sync reports five counts. The one to watch is skipped: it means the file held keys this project does not define, so their translations went nowhere. A non-zero skipped on a translation push almost always means the wrong destination file, or a source push that never happened — not \"already up to date\"."))
    },

    DocEntry("inspect", tr("docs.inspect_and_export", "Inspect and export")) {
        CommandTable(
            caption = t("docs.inspection_commands", "Inspection commands"),
            rows = listOf(
                CommandRow(
                    "lingvo keys list",
                    "--file --search --state --limit",
                    t("docs.lists_and_filters_keys", "Lists and filters keys."),
                ),
                CommandRow(
                    "lingvo qa",
                    "--locale --fail-on-error",
                    t("docs.lists_qa_issues_with_fail_on", "Lists QA issues. With --fail-on-error, exits 1 when any ERROR-severity issue exists — the CI gate."),
                ),
                CommandRow(
                    "lingvo export",
                    "--locale --file --format --bundle --out",
                    t("docs.writes_one_exported_file_or_a", "Writes one exported file, or a zip of every locale."),
                ),
                CommandRow(
                    "lingvo usage",
                    "",
                    t("docs.plan_billable_seats_and_machine_translation", "Plan, billable seats, and machine-translation credits left this month."),
                ),
            ),
        )
    },

    DocEntry("machine-translation", tr("docs.machine_translation", "Machine translation")) {
        DocsProse(t("docs.six_providers_are_supported_mymemory_and", "Six providers are supported. mymemory and libretranslate need no key, so machine translation works before any setup at all."))
        CommandTable(
            caption = t("docs.machine_translation_commands", "Machine-translation commands"),
            rows = listOf(
                CommandRow(
                    "lingvo providers list",
                    "",
                    t("docs.shows_configured_providers_never_prints_keys", "Shows configured providers. Never prints keys."),
                ),
                CommandRow(
                    "lingvo providers set <id>",
                    "--key-env --base-url --model --disable",
                    t("docs.configures_a_provider_the_secret_is", "Configures a provider. The secret is read from stdin or --key-env, never argv."),
                ),
                CommandRow(
                    "lingvo providers test <id>",
                    "",
                    t("docs.one_string_round_trip_against_the", "One-string round trip against the provider."),
                ),
                CommandRow(
                    "lingvo translate",
                    "--locale --provider --only-empty --tone --dry-run",
                    t("docs.machine_translates_and_streams_progress_results", "Machine-translates and streams progress. Results failing placeholder or length validation are reported, never saved."),
                ),
            ),
        )
        DocsProse(t("docs.provider_ids_claude_openai_deepl_google", "Provider ids: claude, openai, deepl, google, libretranslate, mymemory."))
        DocsSubheading(t("docs.what_credits_are", "What credits are"))
        DocsProse(t("docs.one_credit_is_one_string_of", "One credit is one string of up to 1,000 characters, machine-translated into one language; Claude and OpenAI cost three. Longer strings cost one more credit per further 1,000 characters, and 2,000 characters is the per-string limit — past that the model drifts and the result is not reviewable. A plural is charged once, for its longest form, not once per category. A project using its own provider key spends none, because Lingvo is not paying for those calls. Running out never stops you translating by hand, importing, exporting or running QA."))
    },

    DocEntry("codegen", tr("docs.typed_keys", "Typed keys")) {
        DocsProse(t("docs.lingvo_generate_reads_the_generate_block", "lingvo generate reads the generate: block and emits typed accessors, so a renamed key becomes a compile error instead of a blank label in production. Name collisions are a hard error naming both keys — never a silent rename."))
        DocsCode(
            """
            generate:
              - target: kotlin-object
                output: shared/src/commonMain/kotlin/app/Strings.kt
                package: app
              - target: swift-enum
                output: iosApp/Generated/L10n.swift
            """.trimIndent(),
        )
        DocsProse(t("docs.keys_are_fetched_once_and_written", "Keys are fetched once and written to every target. Two targets pointing at the same file is a configuration error, since whichever ran last would silently win."))
    },

    DocEntry("ci", tr("docs.in_ci", "In CI")) {
        DocsProse(t("docs.set_lingvo_token_as_a_secret", "Set LINGVO_TOKEN as a secret and nothing is written to disk. Branch on exit codes rather than message text — they are a stable contract."))
        DocsCode(
            """
            - name: Pull translations and gate on QA
              env:
                LINGVO_TOKEN: ${'$'}{{ secrets.LINGVO_TOKEN }}
              run: |
                lingvo pull
                lingvo qa --fail-on-error
            """.trimIndent(),
        )
        LTable(caption = t("docs.exit_codes", "Exit codes")) {
            LTableHead {
                LTableRow {
                    LTableHeaderCell { Text(t("docs.code", "Code")) }
                    LTableHeaderCell { Text(t("docs.meaning", "Meaning")) }
                    LTableHeaderCell { Text(t("docs.retry", "Retry?")) }
                }
            }
            LTableBody {
                // The em dash stays a literal: it is punctuation standing in for "not applicable",
                // and there is nothing in it for a translator to translate.
                ExitCode("0", t("docs.succeeded", "Succeeded."), "—")
                ExitCode(
                    "1",
                    t("docs.ran_and_reported_an_expected_failure", "Ran, and reported an expected failure: QA errors with --fail-on-error, sync conflicts, rejected machine translations."),
                    t("docs.no_fix_the_content", "No — fix the content."),
                )
                ExitCode(
                    "2",
                    t("docs.bad_flag_bad_argument_or_invalid", "Bad flag, bad argument, or invalid lingvo.yaml."),
                    t("docs.no_fix_the_command", "No — fix the command."),
                )
                ExitCode(
                    "3",
                    t("docs.no_credential_or_the_server_rejected", "No credential, or the server rejected it (HTTP 401/403)."),
                    t("docs.no_re_authenticate", "No — re-authenticate."),
                )
                ExitCode(
                    "4",
                    t("docs.server_unreachable_timed_out_rate_limited", "Server unreachable, timed out, rate-limited, or 5xx."),
                    t("docs.yes_with_backoff", "Yes, with backoff."),
                )
            }
        }
        DocsProse(t("docs.every_command_also_takes_json_which", "Every command also takes --json, which prints a single stable envelope on stdout while human-readable progress stays on stderr."))
    },

    DocEntry("formats", tr("docs.formats", "Formats")) {
        DocsProse(t("docs.every_format_works_in_both_directions", "Every format works in both directions, so you keep the native file type for each platform instead of reshaping your localization stack around the tool."))
        DocsList(
            listOf(
                t("docs.android_xml_android_string_resources_including", "android_xml — Android string resources, including plurals and locale qualifiers"),
                t("docs.ios_strings_ios_stringsdict_xcstrings_apple", "ios_strings, ios_stringsdict, xcstrings — Apple .strings, .stringsdict and String Catalogs"),
                t("docs.json_nested_json_flat_the_two", "json_nested, json_flat — the two shapes web projects actually use"),
                t("docs.properties_java_and_spring_resource_bundles", "properties — Java and Spring resource bundles"),
                t("docs.yaml_rails_and_generic_yaml_catalogues", "yaml — Rails and generic YAML catalogues"),
                t("docs.csv_spreadsheets_for_the_people_who", "csv — spreadsheets, for the people who insist"),
                t("docs.xliff_po_the_standard_interchange_formats", "xliff, po — the standard interchange formats, for moving work in or out"),
            ),
        )
        // The list above says what is supported; the matrix says what each one loses, which is the
        // question a team that has been burned before actually asks. It used to be named as a path
        // in the middle of a sentence rendered as text, so the page's one route onward was a string
        // to retype. The sentence and the link are each a whole string: a translator is never
        // handed half a sentence and asked to guess where the other half will land.
        DocsProse {
            Text(t("docs.what_each_format_carries_plurals_comments", "What each format carries — plurals, comments, placeholder syntax, and what does not survive a round trip — is read from the parsers themselves."))
            Text(" ")
            DocsInlineLink("/docs/formats", t("docs.read_the_format_matrix", "Read the format matrix"))
            Text(".")
        }
    },

    // **The tenth section, and nothing public mentioned it.** Every capability the agent surface
    // has was reachable only by somebody who already knew it existed.
    DocEntry("agents", tr("docs.coding_agents", "Coding agents")) {
        DocsProse { DocsInlineLink("/docs/mcp", t("docs.mcp_complete_reference", "Read the complete MCP setup guide and tool reference")) }
        DocsProse(t("docs.lingvo_speaks_model_context_protocol_so", "Lingvo speaks Model Context Protocol, so an agent working in your repository can read and write strings the same way you do from the CLI. One command sets it up:"))
        DocsCode("lingvo agent install --client claude")
        DocsProse(t("docs.that_writes_the_clients_config_and", "That writes the client's config and a skill generated from the CLI's own command classification, so the instructions cannot describe a command that does not exist or miss one that does."))
        DocsProse(t("docs.the_thing_worth_knowing_is_what", "The thing worth knowing is what the agent gets to read. translation_brief hands it the same context bundle Lingvo assembles for a paid machine-translation provider — your style guide, the glossary terms that occur in the text, the CLDR plural categories the language actually uses, and for each string its developer context, its length budget, its placeholders and where it appears in your code. It is free and it spends no credits."))
        DocsProse(t("docs.and_an_agent_proposes_a_person", "And an agent proposes; a person approves. There is no tool on this surface that can approve a translation, delete anything, or touch members, billing or provider keys — those do not exist here rather than being refused, which is the only defence against prompt injection that does not depend on a model behaving."))
        DocsProse(t("docs.for_a_fleet_that_runs_without", "For a fleet that runs without you, create an agent connection in Settings → Agents. It carries its own scopes, a project allowlist, an autonomy level and a credit budget, all resolved on every call."))
        DocsProse {
            Text(t("docs.the_full_page_coding_agents", "The full page:"))
            Text(" ")
            DocsInlineLink("/agents", t("docs.coding_agents", "Coding agents"))
            Text(".")
        }
    },
)

private class CommandRow(val command: String, val options: String, val description: String)

@Composable
private fun CommandTable(caption: String, rows: List<CommandRow>) {
    LTable(caption = caption) {
        LTableHead {
            LTableRow {
                LTableHeaderCell { Text(t("docs.command", "Command")) }
                LTableHeaderCell { Text(t("docs.key_options", "Key options")) }
                LTableHeaderCell { Text(t("docs.what_it_does", "What it does")) }
            }
        }
        LTableBody {
            rows.forEach { row ->
                LTableRow {
                    LTableCell(Modifier.monoFont().styleModifier { property("white-space", "nowrap") }) {
                        Text(row.command)
                    }
                    LTableCell(
                        Modifier
                            .monoFont()
                            .fontSize(TypeScale.Micro)
                            .color(ColorMode.current.toSitePalette().textSecondary),
                    ) { Text(row.options.ifBlank { "—" }) }
                    LTableCell(Modifier.color(ColorMode.current.toSitePalette().textSecondary)) {
                        Text(row.description)
                    }
                }
            }
        }
    }
}

@Composable
private fun CredentialSource(source: String, howTo: String, useWhen: String) {
    LTableRow {
        LTableCell(Modifier.monoFont().styleModifier { property("white-space", "nowrap") }) {
            Text(source)
        }
        LTableCell(Modifier.monoFont().fontSize(TypeScale.Micro)) { Text(howTo) }
        LTableCell(Modifier.color(ColorMode.current.toSitePalette().textSecondary)) { Text(useWhen) }
    }
}

@Composable
private fun ExitCode(code: String, meaning: String, retry: String) {
    LTableRow {
        LTableCell(Modifier.padding(leftRight = Space.LG)) { DocsInlineCode(code) }
        LTableCell(Modifier.color(ColorMode.current.toSitePalette().textSecondary)) { Text(meaning) }
        LTableCell(Modifier.styleModifier { property("white-space", "nowrap") }) { Text(retry) }
    }
}
